import React from 'react';

import PropTypes from 'prop-types';
import { PiCoatHanger } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { StoreGroupIds } from '~/shared/constants/storeGroups';
import { getStoreGroupCopyConfig } from '~/shared/utils/getStoreGroupCopyConfig';
import {
  mobile,
  useBreakpoint,
  FableticsLogo,
  YittyLogo,
  Button,
} from '~/techstyle-shared/react-components';

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.black};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mobile`
    justify-content: center;
  `}
`;

const ErrorHeading = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 7px;
  padding-left: 94px;
  padding-top: 102px;
  ${mobile`
    position: absolute;
    top: 59px;
    left: 32px;
    padding: 0;
    gap: 4px;
  `}
`;

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 108px;
  margin-bottom: 114px;
  max-width: 850px;

  ${mobile`
    align-items: center;
    margin: 0 53px;
  `}
`;

const StyledFableticsLogo = styled(FableticsLogo)`
  height: 24px;
  width: auto;
  fill: ${({ theme }) => theme.colors.palette.gray};
  ${mobile`
    height: 12px;
  `}
`;

const VerticalLine = styled.div`
  border-left: 2px solid ${({ theme }) => theme.colors.palette.gray};
  height: 52px;
  margin: 0 16px; /* spacing on left and right */
  ${mobile`
    height: 25px;
  `}
`;

const StyledYittyLogo = styled(YittyLogo)`
  height: 40px;
  width: auto;
  fill: ${({ theme }) => theme.colors.palette.gray};
  ${mobile`
    height: 19px;
  `}
`;

const StyledCoatIcon = styled(PiCoatHanger)`
  color: ${({ theme }) => theme.colors.palette.lighterGray};
  margin-bottom: 22px;
  ${mobile`
    margin-bottom: 9px;
  `}
`;

const ErrorCopy1 = styled.p`
  color: ${({ theme }) => theme.colors.palette.white};
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.13;
  margin-bottom: 22px;
  ${mobile`
    text-align: center;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 1.19;
    margin-bottom: 9px;
  `}
`;
const ErrorCopy2 = styled.p`
  color: ${({ theme }) => theme.colors.palette.lighterGray};
  font-size: 40px;
  font-style: normal;
  font-weight: 350;
  margin-bottom: 42px;
  ${mobile`
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 350;
  `}
`;
const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 59px;

  ${mobile`
    flex-direction: column;
    gap: 32px;
  `}
`;

const BodyBlock = styled.div`
  ${mobile`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 9px;
  `}
`;

const buttonLabelStyle = css`
  display: block;
  padding: 14px 20px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.28;
  text-align: center;

  ${mobile`
    font-size: 14px;
    padding: 11px 15px;
  `};
`;

const StyledButton = styled(Button).attrs({ variant: 'unstyled' })`
  color: ${({ theme }) => theme.colors.palette.black};
  background: ${({ theme }) => theme.colors.palette.white};
  border-radius: 4px;
`;

const HeadingBlock = () => {
  return (
    <ErrorHeading>
      <StyledFableticsLogo />
      <VerticalLine />
      <StyledYittyLogo />
    </ErrorHeading>
  );
};

const BodyBlockContent = ({ copyConfig }) => {
  const { isMobile } = useBreakpoint();
  return (
    <>
      <StyledCoatIcon size={isMobile ? 38 : 97} />
      {copyConfig.message1 && <ErrorCopy1>{copyConfig.message1}</ErrorCopy1>}
      {copyConfig.message1 && <ErrorCopy2>{copyConfig.message2}</ErrorCopy2>}
    </>
  );
};
BodyBlockContent.propTypes = {
  copyConfig: PropTypes.object,
};

const FooterBlock = ({ copyConfig }) => {
  return (
    <ButtonsContainer>
      {copyConfig.button1Href && (
        <StyledButton
          href={copyConfig.button1Href}
          labelStyle={buttonLabelStyle}
        >
          {copyConfig.button1Text}
        </StyledButton>
      )}
      {copyConfig.button2Href && (
        <StyledButton
          href={copyConfig.button2Href}
          labelStyle={buttonLabelStyle}
        >
          {copyConfig.button2Text}
        </StyledButton>
      )}
    </ButtonsContainer>
  );
};
FooterBlock.propTypes = {
  copyConfig: PropTypes.object,
};

export default function NotFound({ className }) {
  const storeGroupId =
    useSelector((state) => state.storeGroup?.storeGroupId) ||
    StoreGroupIds.FLUS;
  const copyConfig = getStoreGroupCopyConfig({
    configKey: 'public.fourOhFourPageCopy',
    storeGroupId,
  });

  return (
    <Wrapper className={className}>
      <HeadingBlock />
      <ErrorContent>
        <BodyBlock>
          <BodyBlockContent copyConfig={copyConfig} />
        </BodyBlock>
        <FooterBlock copyConfig={copyConfig} />
      </ErrorContent>
    </Wrapper>
  );
}
NotFound.propTypes = {
  className: PropTypes.string,
};
